// Eskom PARCC — application header.
//
// Brand block on the left; on the right, the signed-in user's email, a role
// chip (admin/reviewer), and the account actions: "Change password" (opens the
// controlled ChangePassword modal — `open` state lives here) and "Sign out".
// There is no backend-URL / config / pull / sync control: this is a Firebase +
// SQL-native app, not the old self-hosted monolith.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun, LogOut, Search, Settings, Sparkles } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme';
import { canAccessRoute } from '../../lib/permissions';
import { AccountModal } from '../auth/AccountModal';
import { ViewAsControl } from './ViewAsControl';
import { NotificationBell } from './NotificationBell';

/** Initials from a display name or email local part, for the account avatar. */
function initialsOf(name: string): string {
  const parts = name
    .replace(/[._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Header({
  onOpenPalette,
}: {
  /** Opens the shared command palette (`/`). Present in BOTH themes — the
   *  palette is a capability, not Lumen decoration. */
  onOpenPalette?: () => void;
}): React.JSX.Element {
  const { user, role, realRole, grant, signOutUser } = useAuth();
  const { theme, toggle } = useTheme();
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || 'Account';

  return (
    <header>
      <div className="brand">
        {/* The wordmark carries the name + tagline. Swap to the white logo in
            dark mode so it reads on the dark header; the black logo in light. */}
        <img
          className="brand-logo"
          src={theme === 'dark' ? '/logos/eskom-parcc-wht.svg' : '/logos/eskom-parcc-blk.svg'}
          alt="Eskom PARCC — Procurement Audit Recovery Command Centre"
        />
      </div>

      <div className="header-account">
        {/* Admin-only: preview the app as another role. Mounted only for admins so
            the access-grants query never fires for non-admins. */}
        {realRole === 'admin' && <ViewAsControl />}
        {/* Search / command palette — the same surface Lumen opens from its
            header widget, so neither theme has a capability the other lacks. */}
        {onOpenPalette && (
          <button
            type="button"
            className="btn ghost icon-only"
            onClick={onOpenPalette}
            aria-label="Search"
            title="Search (press /)"
          >
            <Search size={16} aria-hidden />
          </button>
        )}
        {/* In-app notification centre (4a) — the caller's own alerts. */}
        <NotificationBell />
        <button
          type="button"
          className="btn ghost icon-only"
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-pressed={theme === 'light'}
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {theme === 'dark' ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
        </button>
        {/* Ask AI — the same capability Lumen offers, gated the same way
            (canAccessRoute, i.e. AccessGrant.aiEnabled) so neither theme has an
            entry point the other lacks. Never gated on the AI Mode preference:
            that is a localStorage flag deciding what you see, not a permission. */}
        {canAccessRoute('aiworkspace', grant) && (
          <Link
            to="/aiworkspace"
            className="btn primary icon-only"
            aria-label="Ask AI"
            title="Ask AI"
          >
            <Sparkles size={15} aria-hidden />
          </Link>
        )}
        {/* Settings is where the Legacy/Lumen switch lives — it MUST be
            reachable from Legacy, or a user could never leave it. Change password
            lives inside Settings too, so it is no longer a separate header button. */}
        <Link to="/settings" className="btn ghost" title="Settings">
          <Settings size={15} aria-hidden /> Settings
        </Link>
        {/* Identity consolidated into an initials avatar (mirrors the Lumen
            header): the email + role ride the tooltip, and the avatar opens the
            account modal — replacing the raw email span, the role chip and the
            separate "My account" button. */}
        <button
          type="button"
          className="header-avatar"
          onClick={() => setAccountModalOpen(true)}
          aria-label="My account"
          title={`${user?.email ?? displayName}${role ? ` · ${role}` : ''}`}
        >
          {user?.photoURL ? <img src={user.photoURL} alt="" /> : initialsOf(displayName)}
        </button>
        {/* Icon-only, matching Lumen. `aria-label` IS the accessible name once
            the text is gone — the theme-parity test matches on it. */}
        <button
          type="button"
          className="btn icon-only"
          onClick={() => void signOutUser()}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={15} aria-hidden />
        </button>
      </div>

      <AccountModal open={accountModalOpen} onClose={() => setAccountModalOpen(false)} />
    </header>
  );
}
